import { useCallback, useState } from "react";
import { cn } from "../../lib/cn";
import { api, type PreviewDataset, type PreviewSchemaField } from "../../lib/api";
import type { TagDrawerInfo } from "./types";
import { ChevronIcon, SlidersIcon, InspectIcon } from "./icons";
import { Th, Td, EmptySection, SubTypeList } from "./ui";
import { AssetPropertiesDrawer, TagPropertiesDrawer } from "./Drawers";

function TagPageNumbers({
  current,
  total,
  onSelect,
  loading,
}: {
  current: number;
  total: number;
  onSelect: (page: number) => void;
  loading: boolean;
}) {
  const pages: (number | "…")[] = [];
  if (total <= 7) {
    for (let i = 0; i < total; i++) pages.push(i);
  } else {
    pages.push(0);
    if (current > 2) pages.push("…");
    for (let i = Math.max(1, current - 1); i <= Math.min(total - 2, current + 1); i++) {
      pages.push(i);
    }
    if (current < total - 3) pages.push("…");
    pages.push(total - 1);
  }

  return (
    <>
      {pages.map((p, i) =>
        p === "…" ? (
          <span key={"ellipsis-" + i} className="px-1 text-[12px] text-ash/50 select-none">
            …
          </span>
        ) : (
          <button
            key={p}
            disabled={loading}
            onClick={() => onSelect(p)}
            className={cn(
              "flex h-7 min-w-[28px] items-center justify-center rounded px-1.5 text-[12px] font-medium transition-colors",
              p === current
                ? "bg-accent text-white"
                : "text-ash hover:bg-rule hover:text-ink disabled:opacity-50",
            )}
          >
            {p + 1}
          </button>
        ),
      )}
    </>
  );
}

export function AssetsTab({ datasets, sourceId }: { datasets: PreviewDataset[]; sourceId: string }) {
  const [expandedUrn, setExpandedUrn] = useState<string | null>(null);
  const [drawerDataset, setDrawerDataset] = useState<PreviewDataset | null>(null);
  const [drawerTag, setDrawerTag] = useState<TagDrawerInfo | null>(null);

  const [pageCache, setPageCache] = useState<Map<string, Map<number, PreviewSchemaField[]>>>(new Map());
  const [currentPages, setCurrentPages] = useState<Map<string, number>>(new Map());
  const [loadingUrn, setLoadingUrn] = useState<string | null>(null);

  const TAG_PAGE_SIZE = 500;

  const openAssetDrawer = useCallback((d: PreviewDataset) => {
    setDrawerTag(null);
    setDrawerDataset(d);
  }, []);

  const openTagDrawer = useCallback((info: TagDrawerInfo) => {
    setDrawerDataset(null);
    setDrawerTag(info);
  }, []);

  const closeDrawers = useCallback(() => {
    setDrawerDataset(null);
    setDrawerTag(null);
  }, []);

  const goToTagPage = useCallback(async (d: PreviewDataset, targetPage: number) => {
    if (targetPage === 0 || pageCache.get(d.urn)?.has(targetPage)) {
      setCurrentPages((prev) => new Map(prev).set(d.urn, targetPage));
      return;
    }
    setLoadingUrn(d.urn);
    try {
      const offset = targetPage * TAG_PAGE_SIZE;
      const result = await api.get<{ fields: PreviewSchemaField[]; total: number }>(
        `/api/v1/sources/${encodeURIComponent(sourceId)}/preview/fields?datasetUrn=${encodeURIComponent(d.urn)}&offset=${offset}&limit=${TAG_PAGE_SIZE}`,
      );
      setPageCache((prev) => {
        const next = new Map(prev);
        const dp = new Map(prev.get(d.urn) ?? []);
        dp.set(targetPage, result.fields);
        next.set(d.urn, dp);
        return next;
      });
      setCurrentPages((prev) => new Map(prev).set(d.urn, targetPage));
    } finally {
      setLoadingUrn(null);
    }
  }, [pageCache, sourceId]);

  if (datasets.length === 0) {
    return <EmptySection label="No assets were emitted by this source." />;
  }

  return (
    <div className="space-y-3">
      <div className="card overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="border-b border-rule bg-paper text-left">
            <tr>
              <Th>Asset</Th>
              <Th className="w-40">Platform</Th>
              <Th className="w-64">Types</Th>
              <Th className="w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            {datasets.map((d) => {
              const isExpanded = expandedUrn === d.urn;
              const hasSchema = (d.schema?.length ?? 0) > 0;

              return (
                <>
                  <tr
                    key={d.urn}
                    onClick={() => hasSchema && setExpandedUrn(isExpanded ? null : d.urn)}
                    className={cn(
                      "group transition-colors",
                      hasSchema ? "cursor-pointer hover:bg-paper" : "cursor-default",
                      isExpanded && "bg-paper",
                    )}
                  >
                    <Td>
                      <div className="flex items-center gap-2">
                        <span className="shrink-0">
                          {hasSchema ? <ChevronIcon expanded={isExpanded} /> : <span className="block h-4 w-4" />}
                        </span>
                        <span className="font-medium text-ink">{d.name || "—"}</span>
                      </div>
                    </Td>
                    <Td>
                      <span className="chip-neutral text-[11px]">{d.platform}</span>
                    </Td>
                    <Td>
                      <SubTypeList types={d.subTypes} />
                    </Td>
                    <Td>
                      <button
                        onClick={(e) => { e.stopPropagation(); openAssetDrawer(d); }}
                        title="View asset properties"
                        className="rounded-md p-1.5 text-ash hover:text-ink hover:bg-rule transition-all"
                      >
                        <SlidersIcon />
                      </button>
                    </Td>
                  </tr>

                  {isExpanded && hasSchema && (() => {
                    const totalCount = d.fieldsTotal ?? (d.schema?.length ?? 0);
                    const totalTagPages = Math.ceil(totalCount / TAG_PAGE_SIZE);
                    const curPage = currentPages.get(d.urn) ?? 0;
                    const visibleFields =
                      curPage === 0
                        ? (d.schema ?? [])
                        : (pageCache.get(d.urn)?.get(curPage) ?? null);
                    const isLoadingThis = loadingUrn === d.urn;
                    const rangeStart = curPage * TAG_PAGE_SIZE + 1;
                    const rangeEnd = Math.min((curPage + 1) * TAG_PAGE_SIZE, totalCount);
                    return (
                      <tr key={d.urn + "-tags"}>
                        <td colSpan={4} className="p-0 border-b border-rule">
                          <div className="flex items-center justify-between bg-paper/70 px-5 py-2 border-b border-rule/40">
                            <span className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.16em] text-ash">
                              Tags &nbsp;{totalCount.toLocaleString()}
                            </span>
                            {totalTagPages > 1 && (
                              <span className="text-[11px] text-ash">
                                {rangeStart}–{rangeEnd} of {totalCount.toLocaleString()}
                              </span>
                            )}
                          </div>
                          <div className="overflow-y-auto" style={{ maxHeight: 480 }}>
                            <div className="sticky top-0 z-10 flex items-center border-b border-rule bg-paper pl-5 text-left">
                              <span className="w-8 shrink-0" />
                              <span className="min-w-0 flex-1 py-2 pr-4 font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-ash">
                                Tag Name
                              </span>
                              <span className="w-28 shrink-0 py-2 pr-4 font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-ash">
                                Data Type
                              </span>
                              <span className="min-w-0 flex-1 py-2 pr-4 font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-ash">
                                Description
                              </span>
                              <span className="w-10 shrink-0" />
                            </div>
                            {isLoadingThis ? (
                              <div className="flex items-center justify-center py-10 text-[13px] text-ash">
                                Loading…
                              </div>
                            ) : (visibleFields ?? []).map((field, fi) => {
                              const hasTagProps = (field.structuredProps?.length ?? 0) > 0;
                              return (
                                <div
                                  key={fi}
                                  className="group/tag flex items-center border-b border-rule/30 bg-white pl-5 transition-colors hover:bg-paper/60 last:border-b-0"
                                >

                                  <span className="w-8 shrink-0" />
                                  <span className="min-w-0 flex-1 truncate py-2.5 pr-4 font-mono text-[12px] text-ink">
                                    {field.path}
                                  </span>
                                  <span className="w-28 shrink-0 py-2.5 pr-4 font-mono text-[11.5px] text-ash">
                                    {field.nativeType}
                                  </span>
                                  <span className="min-w-0 flex-1 truncate py-2.5 pr-4 text-[12px] text-ash/70">
                                    {field.description || <span className="select-none text-rule">—</span>}
                                  </span>
                                  <span className="w-10 shrink-0 py-2.5 pr-3">
                                    {hasTagProps && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openTagDrawer({ field, assetName: d.name });
                                        }}
                                        title="View tag structured properties"
                                        className="rounded-md p-1.5 text-ash hover:text-ink hover:bg-rule transition-all"
                                      >
                                        <InspectIcon />
                                      </button>
                                    )}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                          {totalTagPages > 1 && (
                            <div
                              className="flex items-center justify-center gap-1 border-t border-rule/40 bg-paper/50 px-4 py-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                disabled={curPage === 0 || isLoadingThis}
                                onClick={() => void goToTagPage(d, curPage - 1)}
                                className="flex h-7 w-7 items-center justify-center rounded text-[12px] text-ash hover:bg-rule hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              >
                                ‹
                              </button>
                              <TagPageNumbers
                                current={curPage}
                                total={totalTagPages}
                                onSelect={(p) => void goToTagPage(d, p)}
                                loading={isLoadingThis}
                              />
                              <button
                                disabled={curPage === totalTagPages - 1 || isLoadingThis}
                                onClick={() => void goToTagPage(d, curPage + 1)}
                                className="flex h-7 w-7 items-center justify-center rounded text-[12px] text-ash hover:bg-rule hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              >
                                ›
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })()}
                </>
              );
            })}
          </tbody>
        </table>
      </div>

      <AssetPropertiesDrawer dataset={drawerDataset} onClose={closeDrawers} />
      <TagPropertiesDrawer tagInfo={drawerTag} onClose={closeDrawers} />
    </div>
  );
}
